import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Mail,
  Shield,
  ShieldCheck,
  ShieldX,
  Key,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Copy,
  Check,
  ChevronRight,
  Lightbulb,
  Server,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { IssuesList, type Issue } from "../IssuesList";
import { GradeCard } from "../ModuleScore";
import { RecommendationsList, type Recommendation } from "../RecommendationsList";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../card";

// Email Module Data Types
interface SpfData {
  record?: string;
  valid?: boolean;
  all_qualifier?: string;
  dns_lookups?: number;
  mechanisms?: string[];
  issues?: string[];
}

interface DkimSelector {
  selector: string;
  key_size?: number;
  algorithm?: string;
  valid?: boolean;
}

interface DkimData {
  selectors_found?: DkimSelector[];
  issues?: string[];
}

interface DmarcData {
  record?: string;
  policy?: "none" | "quarantine" | "reject";
  subdomain_policy?: "none" | "quarantine" | "reject";
  pct?: number;
  rua?: string[];
  ruf?: string[];
  issues?: string[];
}

// Extended MX record with priority and provider info
interface MxRecord {
  hostname: string;
  priority: number;
  provider?: string;
  ip_addresses?: string[];
}

export interface EmailModuleData {
  score?: number;
  grade?: string;
  spf?: SpfData;
  dkim?: DkimData;
  dmarc?: DmarcData;
  mx_records?: string[] | MxRecord[];
  issues?: Array<string | Issue>;
}

export interface EmailModuleViewProps {
  data: EmailModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}


// Status icon component
function StatusIcon({
  status,
  size = "md",
}: {
  status: "valid" | "invalid" | "missing" | "warning";
  size?: "sm" | "md";
}) {
  const sizeClass = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  switch (status) {
    case "valid":
      return <CheckCircle2 className={cn(sizeClass, "text-emerald-400")} />;
    case "invalid":
      return <XCircle className={cn(sizeClass, "text-red-400")} />;
    case "missing":
      return <ShieldX className={cn(sizeClass, "text-amber-400")} />;
    case "warning":
      return <AlertTriangle className={cn(sizeClass, "text-amber-400")} />;
    default:
      return <Info className={cn(sizeClass, "text-muted-foreground")} />;
  }
}

// Copyable record display
function RecordDisplay({ record, label }: { record?: string; label: string }) {
  const [copied, setCopied] = React.useState(false);

  if (!record) {
    return (
      <div className="p-3 rounded-lg bg-muted/30 border border-border">
        <p className="text-sm text-muted-foreground">No {label} record found</p>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(record);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <span className="text-xs text-muted-foreground font-medium">{label} Record</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleCopy}
        >
          {copied ? (
            <Check className="h-3 w-3 text-emerald-400" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
      <pre className="p-3 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-all">
        {record}
      </pre>
    </div>
  );
}

// DNS Lookup meter
function DnsLookupMeter({ lookups }: { lookups?: number }) {
  const max = 10;
  const current = lookups || 0;
  const percentage = Math.min(100, (current / max) * 100);
  const isOverLimit = current > max;
  const isWarning = current >= 8;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">DNS Lookups</span>
        <span
          className={cn(
            "font-mono font-medium",
            isOverLimit
              ? "text-red-400"
              : isWarning
              ? "text-amber-400"
              : "text-foreground"
          )}
        >
          {current}/{max}
        </span>
      </div>
      <div className="h-2 bg-muted/50 rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isOverLimit
              ? "bg-red-500"
              : isWarning
              ? "bg-amber-500"
              : "bg-emerald-500"
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {isOverLimit && (
        <p className="text-xs text-red-400">
          Exceeds RFC 7208 limit of 10 lookups
        </p>
      )}
    </div>
  );
}

// All-qualifier display
function AllQualifierDisplay({ qualifier }: { qualifier?: string }) {
  const qualifierInfo: Record<string, { label: string; color: string; description: string }> = {
    "-all": {
      label: "Fail",
      color: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
      description: "Strict: Reject unauthorized senders",
    },
    "~all": {
      label: "Soft Fail",
      color: "text-amber-400 bg-amber-500/15 border-amber-500/30",
      description: "Mark as suspicious but accept",
    },
    "?all": {
      label: "Neutral",
      color: "text-slate-400 bg-slate-500/15 border-slate-500/30",
      description: "No policy, accept all",
    },
    "+all": {
      label: "Pass All",
      color: "text-red-400 bg-red-500/15 border-red-500/30",
      description: "Dangerous: Accept from any sender",
    },
  };

  const info = qualifier ? qualifierInfo[qualifier] : null;

  if (!info) {
    return (
      <span className="text-xs text-muted-foreground">
        No all-qualifier found
      </span>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className={cn("text-xs", info.color)}>
          {qualifier}
        </Badge>
        <span className="text-sm font-medium">{info.label}</span>
      </div>
      <p className="text-xs text-muted-foreground">{info.description}</p>
    </div>
  );
}

// DMARC Policy visualization
function DmarcPolicyDisplay({ policy, pct }: { policy?: string; pct?: number }) {
  const policies = ["none", "quarantine", "reject"] as const;
  const currentIndex = policies.indexOf(policy as typeof policies[number]);
  const percentage = pct ?? 100;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        {policies.map((p, index) => (
          <React.Fragment key={p}>
            <div
              className={cn(
                "flex-1 h-8 rounded flex items-center justify-center text-xs font-medium transition-colors",
                index <= currentIndex
                  ? p === "reject"
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                    : p === "quarantine"
                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                    : "bg-slate-500/20 text-slate-400 border border-slate-500/40"
                  : "bg-muted/30 text-muted-foreground border border-border"
              )}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </div>
            {index < policies.length - 1 && (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Enforcement:</span>
        <div className="flex-1 h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-cyan-500 rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs font-mono text-cyan-400">{percentage}%</span>
      </div>
    </div>
  );
}

// DKIM Selector card
function DkimSelectorCard({ selector }: { selector: DkimSelector }) {
  const isWeakKey = selector.key_size && selector.key_size < 2048;

  return (
    <div
      className={cn(
        "p-4 rounded-lg border",
        isWeakKey
          ? "border-amber-500/30 bg-amber-500/5"
          : "border-border bg-muted/30"
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-sm">{selector.selector}</span>
        </div>
        {selector.valid !== undefined && (
          <StatusIcon status={selector.valid ? "valid" : "invalid"} size="sm" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Key Size</p>
          <p
            className={cn(
              "font-mono",
              isWeakKey ? "text-amber-400" : "text-foreground"
            )}
          >
            {selector.key_size ? `${selector.key_size} bits` : "Unknown"}
            {isWeakKey && (
              <AlertTriangle className="inline-block ml-1 h-3 w-3" />
            )}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Algorithm</p>
          <p className="font-mono">{selector.algorithm || "RSA"}</p>
        </div>
      </div>
      {isWeakKey && (
        <p className="mt-3 text-xs text-amber-400">
          Key size should be at least 2048 bits
        </p>
      )}
    </div>
  );
}

// Convert string issues to Issue objects
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];

  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "medium";
      let message = issue;

      // Parse severity prefix from backend (e.g., "[INFO] host: message")
      const prefixMatch = message.match(/^\[(INFO|WARNING|MEDIUM|HIGH|CRITICAL|LOW)\]\s*/i);
      if (prefixMatch) {
        const level = prefixMatch[1].toUpperCase();
        message = message.slice(prefixMatch[0].length);
        if (level === "INFO") severity = "info";
        else if (level === "LOW") severity = "low";
        else if (level === "MEDIUM" || level === "WARNING") severity = "medium";
        else if (level === "HIGH") severity = "high";
        else if (level === "CRITICAL") severity = "critical";
      } else if (
        message.toLowerCase().includes("missing") ||
        message.toLowerCase().includes("not found")
      ) {
        severity = "high";
      } else if (
        message.toLowerCase().includes("weak") ||
        message.toLowerCase().includes("warning")
      ) {
        severity = "medium";
      } else if (message.toLowerCase().includes("recommend")) {
        severity = "low";
      }

      return {
        id: `email-issue-${index}`,
        severity,
        message,
        module: "email",
      };
    }
    return issue;
  });
}

// Normalize MX records (handle both string[] and MxRecord[] formats)
function normalizeMxRecords(records?: string[] | MxRecord[]): MxRecord[] {
  if (!records || records.length === 0) return [];

  // Check if it's already MxRecord format
  if (typeof records[0] === "object" && "hostname" in records[0]) {
    return records as MxRecord[];
  }

  // Convert string format (e.g., "10 mail.example.com") to MxRecord
  return (records as string[]).map((record, index) => {
    const parts = record.trim().split(/\s+/);
    const priority = parts.length > 1 ? parseInt(parts[0], 10) : (index + 1) * 10;
    const hostname = parts.length > 1 ? parts.slice(1).join(" ") : parts[0];

    // Detect common email providers
    let provider: string | undefined;
    const hostLower = hostname.toLowerCase();
    if (hostLower.includes("google") || hostLower.includes("googlemail")) {
      provider = "Google Workspace";
    } else if (hostLower.includes("outlook") || hostLower.includes("microsoft")) {
      provider = "Microsoft 365";
    } else if (hostLower.includes("protonmail")) {
      provider = "ProtonMail";
    } else if (hostLower.includes("mimecast")) {
      provider = "Mimecast";
    } else if (hostLower.includes("barracuda")) {
      provider = "Barracuda";
    } else if (hostLower.includes("mailgun")) {
      provider = "Mailgun";
    } else if (hostLower.includes("sendgrid")) {
      provider = "SendGrid";
    } else if (hostLower.includes("zoho")) {
      provider = "Zoho";
    }

    return {
      hostname,
      priority: isNaN(priority) ? (index + 1) * 10 : priority,
      provider,
    };
  });
}

// MX Records table columns
function getMxColumns(): ColumnDef<MxRecord>[] {
  return [
    {
      id: "priority",
      header: "Priority",
      accessor: (row) => row.priority,
      cell: (value) => (
        <Badge variant="outline" className="font-mono text-xs">
          {value as number}
        </Badge>
      ),
      sortable: true,
      width: "100px",
    },
    {
      id: "hostname",
      header: "Mail Server",
      accessor: (row) => row.hostname,
      cell: (value, row) => (
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          <code className="text-sm font-mono">{value as string}</code>
          {row.provider && (
            <Badge variant="secondary" className="text-xs">
              {row.provider}
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      filterable: true,
    },
  ];
}

// Generate recommendations based on email security status
function generateRecommendations(data: EmailModuleData): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // SPF Recommendations
  if (!data.spf?.record) {
    recommendations.push({
      id: "spf-missing",
      title: "Implement SPF Record",
      description: "No SPF record found. SPF helps prevent email spoofing by specifying which servers can send email on behalf of your domain.",
      priority: "high",
      action: "Add a TXT record starting with 'v=spf1' to your DNS",
    });
  } else {
    if (data.spf.all_qualifier === "+all") {
      recommendations.push({
        id: "spf-plus-all",
        title: "Fix Dangerous SPF Configuration",
        description: "Your SPF record uses '+all' which allows any server to send email as your domain. This completely defeats the purpose of SPF.",
        priority: "high",
        action: "Change '+all' to '-all' for strict enforcement or '~all' for soft fail",
      });
    } else if (data.spf.all_qualifier === "?all") {
      recommendations.push({
        id: "spf-neutral",
        title: "Strengthen SPF Policy",
        description: "Your SPF uses '?all' (neutral) which provides no protection. Consider using '-all' or '~all' for better security.",
        priority: "medium",
        action: "Update SPF to use '-all' (recommended) or '~all'",
      });
    } else if (data.spf.all_qualifier === "~all") {
      recommendations.push({
        id: "spf-softfail",
        title: "Consider Strict SPF Policy",
        description: "Your SPF uses '~all' (soft fail). Once you've confirmed all legitimate senders are included, consider upgrading to '-all'.",
        priority: "low",
        action: "Change '~all' to '-all' after verifying all legitimate mail sources",
      });
    }

    if ((data.spf.dns_lookups || 0) > 10) {
      recommendations.push({
        id: "spf-lookups",
        title: "Reduce SPF DNS Lookups",
        description: `Your SPF record requires ${data.spf.dns_lookups} DNS lookups, exceeding the RFC 7208 limit of 10. This may cause SPF validation failures.`,
        priority: "high",
        action: "Flatten SPF record by replacing includes with direct IP ranges, or use an SPF flattening service",
      });
    }
  }

  // DKIM Recommendations
  if (!data.dkim?.selectors_found || data.dkim.selectors_found.length === 0) {
    recommendations.push({
      id: "dkim-missing",
      title: "Implement DKIM Signing",
      description: "No DKIM records found. DKIM cryptographically signs emails to prove they originated from your domain.",
      priority: "high",
      action: "Configure DKIM signing in your email server and publish the public key in DNS",
    });
  } else {
    const weakKeys = data.dkim.selectors_found.filter(s => s.key_size && s.key_size < 2048);
    if (weakKeys.length > 0) {
      recommendations.push({
        id: "dkim-weak-key",
        title: "Upgrade DKIM Key Size",
        description: `${weakKeys.length} DKIM selector(s) use keys smaller than 2048 bits. Short keys are vulnerable to factoring attacks.`,
        priority: "medium",
        action: "Generate new 2048-bit (or larger) RSA keys for DKIM signing",
      });
    }
  }

  // DMARC Recommendations
  if (!data.dmarc?.record) {
    recommendations.push({
      id: "dmarc-missing",
      title: "Implement DMARC Policy",
      description: "No DMARC record found. DMARC ties together SPF and DKIM, providing clear instructions on how to handle authentication failures.",
      priority: "high",
      action: "Add a DMARC TXT record at _dmarc.yourdomain.com",
    });
  } else {
    if (data.dmarc.policy === "none") {
      recommendations.push({
        id: "dmarc-none",
        title: "Strengthen DMARC Policy",
        description: "DMARC policy is set to 'none' which only monitors without enforcing. Consider moving to 'quarantine' or 'reject'.",
        priority: "medium",
        action: "Gradually increase to 'quarantine' then 'reject' as you verify legitimate sources",
      });
    } else if (data.dmarc.policy === "quarantine") {
      recommendations.push({
        id: "dmarc-quarantine",
        title: "Consider Reject Policy",
        description: "DMARC policy is 'quarantine'. For maximum protection, consider upgrading to 'reject' once you're confident in your configuration.",
        priority: "low",
        action: "Change policy to 'reject' to fully prevent spoofed emails",
      });
    }

    if (!data.dmarc.rua || data.dmarc.rua.length === 0) {
      recommendations.push({
        id: "dmarc-no-rua",
        title: "Enable DMARC Reporting",
        description: "No aggregate reporting (rua) is configured. DMARC reports help you monitor email authentication and identify issues.",
        priority: "medium",
        action: "Add 'rua=mailto:dmarc@yourdomain.com' to receive aggregate reports",
      });
    }

    if (data.dmarc.pct !== undefined && data.dmarc.pct < 100) {
      recommendations.push({
        id: "dmarc-pct",
        title: "Increase DMARC Enforcement",
        description: `DMARC is only enforced on ${data.dmarc.pct}% of emails. Consider increasing to 100% for full protection.`,
        priority: "low",
        action: `Increase pct from ${data.dmarc.pct} to 100 after monitoring reports`,
      });
    }
  }

  return recommendations;
}

export function EmailModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: EmailModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const mxRecords = normalizeMxRecords(data.mx_records);
  const recommendations = generateRecommendations(data);
  const mxColumns = getMxColumns();

  // Determine protocol statuses
  const spfStatus = data.spf?.record
    ? data.spf.valid
      ? "valid"
      : "invalid"
    : "missing";
  const dkimStatus =
    data.dkim?.selectors_found && data.dkim.selectors_found.length > 0
      ? "valid"
      : "missing";
  const dmarcStatus = data.dmarc?.record
    ? data.dmarc.policy === "reject"
      ? "valid"
      : "warning"
    : "missing";

  return (
    <ModuleContainer
      moduleName="email"
      icon={Mail}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      grade={data.grade}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Header with grade */}
        <div className="flex items-center gap-6 p-6 rounded-lg bg-muted/30 border border-border">
          {data.grade && <GradeCard grade={data.grade} score={data.score} />}
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">Email Security Summary</h3>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <StatusIcon status={spfStatus} size="sm" />
                <span className="text-sm">SPF</span>
              </div>
              <div className="flex items-center gap-2">
                <StatusIcon status={dkimStatus} size="sm" />
                <span className="text-sm">DKIM</span>
              </div>
              <div className="flex items-center gap-2">
                <StatusIcon status={dmarcStatus} size="sm" />
                <span className="text-sm">DMARC</span>
              </div>
            </div>
          </div>
        </div>

        {/* Protocol Cards */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* SPF Card */}
          <Card className="bg-card/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-cyan-400" />
                  <CardTitle className="text-base">SPF</CardTitle>
                </div>
                <StatusIcon status={spfStatus} />
              </div>
              <CardDescription>Sender Policy Framework</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RecordDisplay record={data.spf?.record} label="SPF" />
              <DnsLookupMeter lookups={data.spf?.dns_lookups} />
              <AllQualifierDisplay qualifier={data.spf?.all_qualifier} />
              {data.spf?.issues && data.spf.issues.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Issues:</p>
                  <ul className="space-y-1">
                    {data.spf.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-amber-400 flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DKIM Card */}
          <Card className="bg-card/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-cyan-400" />
                  <CardTitle className="text-base">DKIM</CardTitle>
                </div>
                <StatusIcon status={dkimStatus} />
              </div>
              <CardDescription>DomainKeys Identified Mail</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {data.dkim?.selectors_found &&
              data.dkim.selectors_found.length > 0 ? (
                data.dkim.selectors_found.map((selector, index) => (
                  <DkimSelectorCard key={index} selector={selector} />
                ))
              ) : (
                <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-400">
                    <ShieldX className="h-4 w-4" />
                    <span className="text-sm font-medium">No DKIM Found</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    No DKIM selectors were discovered
                  </p>
                </div>
              )}
              {data.dkim?.issues && data.dkim.issues.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Issues:</p>
                  <ul className="space-y-1">
                    {data.dkim.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-amber-400 flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DMARC Card */}
          <Card className="bg-card/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-cyan-400" />
                  <CardTitle className="text-base">DMARC</CardTitle>
                </div>
                <StatusIcon status={dmarcStatus} />
              </div>
              <CardDescription>Domain-based Message Authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RecordDisplay record={data.dmarc?.record} label="DMARC" />
              <DmarcPolicyDisplay
                policy={data.dmarc?.policy}
                pct={data.dmarc?.pct}
              />
              {(data.dmarc?.rua || data.dmarc?.ruf) && (
                <div className="space-y-2 pt-2 border-t border-border">
                  {data.dmarc.rua && data.dmarc.rua.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Aggregate Reports (rua):
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {data.dmarc.rua.map((uri, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-xs font-mono"
                          >
                            {uri}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.dmarc.ruf && data.dmarc.ruf.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Forensic Reports (ruf):
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {data.dmarc.ruf.map((uri, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="text-xs font-mono"
                          >
                            {uri}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {data.dmarc?.issues && data.dmarc.issues.length > 0 && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-2">Issues:</p>
                  <ul className="space-y-1">
                    {data.dmarc.issues.map((issue, i) => (
                      <li key={i} className="text-xs text-amber-400 flex items-start gap-1">
                        <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* MX Records */}
        {mxRecords.length > 0 && (
          <ModuleSection
            title="MX Records"
            icon={Mail}
            badge={
              <Badge variant="outline" className="text-xs">
                {mxRecords.length}
              </Badge>
            }
          >
            <ModuleDataTable
              data={mxRecords}
              columns={mxColumns}
              paginated={mxRecords.length > 10}
              searchable={mxRecords.length > 5}
              searchPlaceholder="Search mail servers..."
            />
          </ModuleSection>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <ModuleSection
            title="Recommendations"
            icon={Lightbulb}
            badge={
              <Badge variant="outline" className="text-xs bg-slate-500/10 border-slate-500/30 text-slate-400">
                {recommendations.length}
              </Badge>
            }
          >
            <RecommendationsList recommendations={recommendations} />
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
              >
                {issues.length}
              </Badge>
            }
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

EmailModuleView.displayName = "EmailModuleView";
